import { useState, useEffect } from 'react'
import {
  X, Folder, FileText, Download, Loader2, ChevronRight,
  ArrowLeft, Cloud, HardDrive, Box
} from 'lucide-react'
import api from '../../core/services/api'

interface CloudFile {
  id: string
  name: string
  path: string
  is_folder: boolean
  size?: number
  mime_type?: string
  modified_at?: string
}

interface Provider {
  provider: string
  name: string
  connected: boolean
  account_name?: string
  icon: string
}

interface Props {
  entityType: string
  entityId: string
  onClose: () => void
  onDownloaded: (allegato: { id: string; filename: string }) => void
  onToast: (msg: string, type: 'success' | 'error') => void
}

const PROVIDER_ICONS: Record<string, typeof Cloud> = {
  cloud: Cloud,
  'hard-drive': HardDrive,
  box: Box,
}

function formatSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function CloudFilePicker({ entityType, entityId, onClose, onDownloaded, onToast }: Props) {
  const [providers, setProviders] = useState<Provider[]>([])
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [files, setFiles] = useState<CloudFile[]>([])
  const [loading, setLoading] = useState(true)
  const [browsing, setBrowsing] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [folderStack, setFolderStack] = useState<{ id: string | null; name: string }[]>([])

  useEffect(() => {
    loadProviders()
  }, [])

  const loadProviders = async () => {
    setLoading(true)
    try {
      const resp = await api.get('/v1/cloud/providers')
      setProviders(resp.data)
    } catch (err) {
      onToast('Errore caricamento provider cloud', 'error')
    } finally {
      setLoading(false)
    }
  }

  const selectProvider = async (provider: string) => {
    setSelectedProvider(provider)
    setFolderStack([{ id: null, name: 'Root' }])
    await browseFolder(provider, null)
  }

  const browseFolder = async (provider: string, folderId: string | null) => {
    setBrowsing(true)
    try {
      const params = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : ''
      const resp = await api.get(`/v1/cloud/${provider}/files${params}`)
      setFiles(resp.data)
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore navigazione cloud', 'error')
    } finally {
      setBrowsing(false)
    }
  }

  const enterFolder = async (folder: CloudFile) => {
    if (!selectedProvider) return
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }])
    await browseFolder(selectedProvider, folder.id)
  }

  const goBack = async () => {
    if (!selectedProvider || folderStack.length <= 1) {
      setSelectedProvider(null)
      setFiles([])
      setFolderStack([])
      return
    }
    const newStack = folderStack.slice(0, -1)
    setFolderStack(newStack)
    const parentId = newStack[newStack.length - 1].id
    await browseFolder(selectedProvider, parentId)
  }

  const downloadFile = async (file: CloudFile) => {
    if (!selectedProvider) return
    setDownloading(file.id)
    try {
      const resp = await api.post(`/v1/cloud/${selectedProvider}/download`, {
        file_id: file.id,
        entity_type: entityType,
        entity_id: entityId,
        titolo: file.name,
      })
      onToast(`"${file.name}" scaricato e allegato`, 'success')
      onDownloaded({ id: resp.data.allegato_id, filename: resp.data.filename })
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore download', 'error')
    } finally {
      setDownloading(null)
    }
  }

  const connectProvider = async (provider: string) => {
    try {
      const resp = await api.get(`/v1/cloud/${provider}/auth-url`)
      window.location.href = resp.data.auth_url
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore connessione', 'error')
    }
  }

  // Vista selezione provider
  const renderProviderList = () => (
    <div className="space-y-3 p-5">
      {providers.length === 0 && !loading ? (
        <p className="text-sm text-gray-500 text-center py-4">
          Nessun provider cloud configurato.
        </p>
      ) : (
        providers.map(p => {
          const IconComp = PROVIDER_ICONS[p.icon] || Cloud
          return (
            <button
              key={p.provider}
              onClick={() => p.connected ? selectProvider(p.provider) : connectProvider(p.provider)}
              className="w-full flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <IconComp className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{p.name}</p>
                {p.connected ? (
                  <p className="text-xs text-green-600">{p.account_name || 'Connesso'}</p>
                ) : (
                  <p className="text-xs text-gray-400">Clicca per collegare</p>
                )}
              </div>
              {p.connected && (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
          )
        })
      )}
    </div>
  )

  // Vista file browser
  const renderFileBrowser = () => (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 px-5 py-2 border-b bg-gray-50 text-sm overflow-x-auto">
        <button onClick={goBack} className="p-1 hover:bg-gray-200 rounded">
          <ArrowLeft className="w-4 h-4" />
        </button>
        {folderStack.map((f, i) => (
          <span key={i} className="flex items-center gap-1 text-gray-600 whitespace-nowrap">
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            <span className={i === folderStack.length - 1 ? 'font-medium text-gray-900' : ''}>
              {f.name}
            </span>
          </span>
        ))}
        {browsing && <Loader2 className="w-4 h-4 animate-spin ml-auto text-gray-400" />}
      </div>

      {/* File list */}
      <div className="flex-1 overflow-y-auto">
        {files.length === 0 && !browsing ? (
          <p className="text-sm text-gray-400 text-center py-8">Cartella vuota</p>
        ) : (
          <div className="divide-y">
            {/* Folders first */}
            {files.filter(f => f.is_folder).map(file => (
              <button
                key={file.id}
                onClick={() => enterFolder(file)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <Folder className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-gray-900 truncate">{file.name}</span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            ))}
            {/* Then files */}
            {files.filter(f => !f.is_folder).map(file => (
              <div
                key={file.id}
                className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{formatSize(file.size)}</p>
                </div>
                <button
                  onClick={() => downloadFile(file)}
                  disabled={!!downloading}
                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Scarica e allega"
                >
                  {downloading === file.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedProvider ? 'Seleziona file' : 'Cloud Storage'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : selectedProvider ? (
            renderFileBrowser()
          ) : (
            renderProviderList()
          )}
        </div>
      </div>
    </div>
  )
}
